import { defineStore } from 'pinia';
import mushodoPromise from '@webapp-next/lib/request/webapp/mushodo-promise';
import { ADD_ON_STATUS_ACTION, ADD_ONS } from '../definitions';
import { useSubscriptionStore } from './subscription';
import { addProduct as addProductV2 } from '../request/add-product';
import {
  addOns,
  addOnDataMap,
  allAddOnDetails,
  countryCodes,
  EVENTS,
  EVENT_SOURCE,
  EVENT_SCREEN,
  PAYMENT_FREQUENCY,
  ADD_ON_ORDERING,
  ADD_ON_SUBSCRIPTION_TYPE,
} from '@/apps/billing-shared/src/definitions';
import { trackAddOnSubscriptionEvent } from '@apps-once/composables/track';
import { trackPromotionAddEvents } from '@apps-once/composables/track-promotion-add';
import { useOnceStore } from '.';
import { useQuoteStore } from './quote';
import { usePlansStore } from '@apps-billing-shared/store/plans';
import { logInfo } from '@/lib/webapp-sdk/logger';
import { ddLogError } from '@/lib/utils/dd-log';

const useAddOnStore = defineStore('once:add-on', {
  state: () => ({
    addOnId: null,
    status: {
      isOutcomeModalVisible: false,
      isCancellationModalVisible: false,
      isTrialToCancel: null,
      isAddOnDowngradeModalVisible: false,
      isAddOnDowngradeImplementationStateModalVisible: false,
      isAddOnCheckoutSuccessModalVisible: false,
      isAnnualPlanRequired: false,
      isCancelAnnualModalVisible: false,
      isCancelAnnualPaidMonthlyModalVisible: false,
      isSubscriptionEndingRestrictionModalVisible: false,
      isAnnualAddOnBillingScheduleChangeModalVisible: false,
      isContractAmendmentModalVisible: false,
      isMoveFromAnnualToMonthly: false,
      isMoveFromMonthlyToAnnual: false,
      isLoading: false,
      isSuccess: false,
      action: '',
    },
    error: {
      subscribe: null,
      cancel: null,
    },
    lastSubscribeParams: {
      hostname: null,
      addOnId: null,
      isTrial: null,
      promoCode: null,
      addOns: null,
    },
  }),
  getters: {
    data: () => ADD_ONS,
  },
  actions: {
    async subscribeToAddOn(
      hostname,
      addOnId,
      isTrial = false,
      promoCode = '',
      addOns,
      isChannelCpq = false
    ) {
      this.status.action = ADD_ON_STATUS_ACTION.SUBSCRIBE;

      this.error.subscribe = null;
      this.error.cancel = null;

      this.status.isSuccess = false;
      this.status.isLoading = true;
      this.resetCancellationModalState();
      this.addOnId = addOnId;

      // Store parameters for potential retry
      this.lastSubscribeParams = {
        hostname,
        addOnId,
        isTrial,
        promoCode,
        addOns,
      };

      // subscribeToAddOn handles the Subscribe and MoveFromMonthlyToAnnual flows
      const isUpdateEvent = this.status.isMoveFromMonthlyToAnnual;

      // PROMOTION_ADD fires one event per distinct code (deduped) via the
      // shared helper; the joined promoCode string is still sent to the backend
      // unchanged. Source distinguishes the add-on trial vs subscribe flow.
      const promoEventSource = isTrial
        ? EVENT_SOURCE.ADD_ON_TRIAL_CHECKOUT
        : EVENT_SOURCE.ADD_ON_SUBSCRIBE_CHECKOUT;
      // Snapshot so all events in this attempt tag against the same set, even
      // though SUCCESS/FAILURE fire after the awaited network round-trip.
      const autoAppliedPromoCodes = [...useQuoteStore().autoAppliedCodes];

      try {
        trackAddOnSubscriptionEvent({
          eventName: isUpdateEvent
            ? EVENTS.ADD_ON_SUBSCRIPTION_UPDATE_REQUESTED
            : EVENTS.ADD_ON_SUBSCRIBE_REQUESTED,
          eventSource: EVENT_SOURCE.COMPLETE_PURCHASE,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          addOnId,
          isTrial,
          addOns,
        });

        trackPromotionAddEvents({
          eventName: EVENTS.PROMOTION_ADD_REQUESTED,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          eventSource: promoEventSource,
          promoCodes: promoCode,
          autoAppliedCodes: autoAppliedPromoCodes,
        });

        const onceStore = useOnceStore();
        const tenantId = onceStore.id;

        if (!tenantId) {
          throw new Error(
            'Tenant ID is required. Please ensure a business is selected.'
          );
        }

        const result = await addProductV2({
          tenantId,
          addOnProductIdentifier: addOnId,
          isTrial,
          promoCode: promoCode || undefined,
        });

        if (!result.success) {
          throw new Error('Failed to add product');
        }

        this.status.isSuccess = true;

        // Clear subscription cache to ensure fresh data on next load
        // We perform this cache clear after the success modal is closed for CPQ channels
        if (!isChannelCpq) {
          useSubscriptionStore().clearCache(hostname);
        }

        trackAddOnSubscriptionEvent({
          eventName: isUpdateEvent
            ? EVENTS.ADD_ON_SUBSCRIPTION_UPDATE_SUCCESS_ACKNOWLEDGED
            : EVENTS.ADD_ON_SUBSCRIBE_SUCCESS_ACKNOWLEDGED,
          eventSource: EVENT_SOURCE.COMPLETE_PURCHASE,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          addOnId,
          isTrial,
          addOns,
        });

        trackPromotionAddEvents({
          eventName: EVENTS.PROMOTION_ADD_SUCCESS_ACKNOWLEDGED,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          eventSource: promoEventSource,
          promoCodes: promoCode,
          autoAppliedCodes: autoAppliedPromoCodes,
        });
      } catch (e) {
        this.error.subscribe = e;
        try {
          ddLogError(
            'Add-on subscription failed',
            {
              metadata: {
                hostname,
                addOnId,
                isTrial,
                promoCode,
                isUpdateEvent,
                errorMessage: e?.message || String(e),
                errorStack: e?.stack,
              },
            },
            e
          );
        } catch (logError) {
          // Silently fail - don't let logging errors mask the original error
          console.error('Failed to log error to Datadog:', logError);
        }
        trackAddOnSubscriptionEvent({
          eventName: isUpdateEvent
            ? EVENTS.ADD_ON_SUBSCRIPTION_UPDATE_FAILURE_ACKNOWLEDGED
            : EVENTS.ADD_ON_SUBSCRIBE_FAILURE_ACKNOWLEDGED,
          eventSource: EVENT_SOURCE.COMPLETE_PURCHASE,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          addOnId,
          errorMessage: this.error.subscribe,
          isTrial,
        });
        trackPromotionAddEvents({
          eventName: EVENTS.PROMOTION_ADD_FAILURE_ACKNOWLEDGED,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          eventSource: promoEventSource,
          promoCodes: promoCode,
          errorMessage: this.error.subscribe,
          autoAppliedCodes: autoAppliedPromoCodes,
        });
      } finally {
        this.status.isLoading = false;
      }
    },
    /**
     * Retries the last subscription attempt using stored parameters.
     * @return {Promise} The subscription result
     */
    async retryLastSubscriptionAttempt() {
      if (!this.lastSubscribeParams.addOnId) {
        const error = new Error('No previous subscription attempt to retry');
        try {
          ddLogError(
            'Add-on retry failed: no previous attempt',
            {
              metadata: {
                lastSubscribeParams: this.lastSubscribeParams,
              },
            },
            error
          );
        } catch (logError) {
          console.error('Failed to log error to Datadog:', logError);
        }
        throw error;
      }

      const { hostname, addOnId, isTrial, promoCode, addOns } =
        this.lastSubscribeParams;

      if (!hostname) {
        const error = new Error(
          'No hostname stored for previous subscription attempt. Cannot retry.'
        );
        try {
          ddLogError(
            'Add-on retry failed: missing hostname',
            {
              metadata: {
                lastSubscribeParams: this.lastSubscribeParams,
                addOnId,
              },
            },
            error
          );
        } catch (logError) {
          // Silently fail - don't let logging errors mask the original error
          console.error('Failed to log error to Datadog:', logError);
        }
        throw error;
      }

      return this.subscribeToAddOn(
        hostname,
        addOnId,
        isTrial,
        promoCode,
        addOns
      );
    },
    async moveFromAnnualToMonthly(hostname) {
      this.status.action = ADD_ON_STATUS_ACTION.SUBSCRIBE;
      this.error.subscribe = null;
      this.error.cancel = null;

      this.status.isSuccess = false;
      this.status.isLoading = true;

      const onceStore = useOnceStore();
      const isTrial = onceStore.isAddOnTrialAvailable(this.addOnId);
      const addOns = this.getFromToAddOns(this.addOnId);
      this.resetCancellationModalState();

      try {
        // Track the update request event
        trackAddOnSubscriptionEvent({
          eventName: EVENTS.ADD_ON_SUBSCRIPTION_UPDATE_REQUESTED,
          eventSource: EVENT_SOURCE.COMPLETE_PURCHASE,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          addOnId: this.addOnId,
          isTrial,
          addOns,
        });

        const payload = {
          Hostname: hostname,
          AddOnProductIdentifier: this.addOnId,
        };
        const result = await mushodoPromise('MoveFromAnnualToMonthly', payload);

        if (result.error) {
          throw result.error;
        }

        this.status.isSuccess = true;

        // Track the success event
        trackAddOnSubscriptionEvent({
          eventName: EVENTS.ADD_ON_SUBSCRIPTION_UPDATE_SUCCESS_ACKNOWLEDGED,
          eventSource: EVENT_SOURCE.COMPLETE_PURCHASE,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          addOnId: this.addOnId,
          isTrial,
          addOns,
        });
      } catch (e) {
        this.error.subscribe = e;

        // Track the failure event
        trackAddOnSubscriptionEvent({
          eventName: EVENTS.ADD_ON_SUBSCRIPTION_UPDATE_FAILURE_ACKNOWLEDGED,
          eventSource: EVENT_SOURCE.COMPLETE_PURCHASE,
          eventScreen: EVENT_SCREEN.CHOOSE_PLAN_CHECKOUT,
          addOnId: this.addOnId,
          errorMessage: this.error.subscribe,
          isTrial,
          addOns,
        });
      } finally {
        this.status.isLoading = false;
      }
    },
    async cancelAddOn({
      hostname,
      addOnId,
      isTrial,
      showOutcomeModal = true,
      mainReason = null,
      additionalFeedback = null,
      alternativeSolution = null,
    }) {
      const onceStore = useOnceStore();
      const resolvedIsTrial = isTrial ?? this.status.isTrialToCancel ?? false;
      const addOnName = allAddOnDetails[addOnId].name;
      const eventSource = resolvedIsTrial
        ? `Cancel ${addOnName} trial confirmation modal`
        : `Cancel ${addOnName} subscription confirmation modal`;
      this.status.action = ADD_ON_STATUS_ACTION.CANCEL;

      this.error.cancel = null;
      this.error.subscribe = null;

      this.status.isSuccess = false;
      this.resetCancellationModalState();

      this.status.isOutcomeModalVisible = showOutcomeModal;
      this.status.isLoading = true;
      this.addOnId = addOnId;

      const hasDowngradeFlag = onceStore.getFeatureFlag(
        'wo-2614-add-on-downgrade'
      );
      const eventPayload = {
        eventSource,
        eventScreen: EVENT_SCREEN.CHOOSE_PLAN_ONCE,
        addOnId,
        isTrial: resolvedIsTrial,
        reasonName: mainReason || undefined,
        alternateSolution: alternativeSolution || undefined,
        reasonDescription: additionalFeedback || undefined,
      };

      let cancelPayload = {
        Hostname: hostname,
        AddOnProductIdentifier: addOnId,
      };

      if (hasDowngradeFlag) {
        cancelPayload = {
          ...cancelPayload,
          Reason: mainReason,
          Feedback: additionalFeedback,
          AlternativeSolution: alternativeSolution,
        };
      }

      try {
        trackAddOnSubscriptionEvent({
          eventName: EVENTS.ADD_ON_UNSUBSCRIBE_REQUESTED,
          ...eventPayload,
        });

        const result = await mushodoPromise('CancelProduct', cancelPayload);

        if (result.error) {
          throw result.error;
        }

        this.status.isSuccess = true;

        trackAddOnSubscriptionEvent({
          eventName: EVENTS.ADD_ON_UNSUBSCRIBE_SUCCESS_ACKNOWLEDGED,
          ...eventPayload,
        });
      } catch (e) {
        this.error.cancel = e;

        trackAddOnSubscriptionEvent({
          eventName: EVENTS.ADD_ON_UNSUBSCRIBE_FAILURE_ACKNOWLEDGED,
          errorMessage: this.error.cancel,
          ...eventPayload,
        });
      } finally {
        this.status.isLoading = false;
      }
    },
    closeModal() {
      this.status.isOutcomeModalVisible = false;
      this.resetCancellationModalState();
      this.status.isAnnualPlanRequired = false;
      this.status.isSubscriptionEndingRestrictionModalVisible = false;
      this.status.isAnnualAddOnBillingScheduleChangeModalVisible = false;
      this.status.isContractAmendmentModalVisible = false;
      if (this.status.isSuccess) {
        window.location.reload();
      }
    },
    openCancellationModal(addOnId, isTrial = false) {
      this.addOnId = addOnId;
      this.status.isTrialToCancel = isTrial;
      this.status.action = ADD_ON_STATUS_ACTION.CANCEL;

      const onceStore = useOnceStore();
      if (onceStore.getFeatureFlag('wo-2614-add-on-downgrade')) {
        this.status.isAddOnDowngradeModalVisible = true;
      } else {
        this.status.isCancellationModalVisible = true;
      }

      trackAddOnSubscriptionEvent({
        eventName: EVENTS.ADD_ON_UNSUBSCRIBE_STARTED,
        eventSource: isTrial
          ? 'Once edit business plan - Add-on trial cancel button'
          : 'Once edit business plan - Add-on cancel button',
        eventScreen: EVENT_SCREEN.CHOOSE_PLAN_ONCE,
        addOnId,
        isTrial,
      });
    },
    openAddOnDowngradeImplementationStateModal(addOnId, isTrial = false) {
      this.addOnId = addOnId;
      this.status.isTrialToCancel = isTrial;
      this.status.action = ADD_ON_STATUS_ACTION.CANCEL;
      this.status.isAddOnDowngradeImplementationStateModalVisible = true;

      trackAddOnSubscriptionEvent({
        eventName: EVENTS.ADD_ON_UNSUBSCRIBE_STARTED,
        eventSource: isTrial
          ? 'Once edit business plan - Add-on trial cancel button'
          : 'Once edit business plan - Add-on cancel button',
        eventScreen: EVENT_SCREEN.CHOOSE_PLAN_ONCE,
        addOnId,
        isTrial,
      });
    },

    openAddOnCheckout(addOnId) {
      this.addOnId = addOnId;
      this.status.action = ADD_ON_STATUS_ACTION.SUBSCRIBE;
      const monthlyAddOnId =
        this.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
          addOnId,
          PAYMENT_FREQUENCY.MONTHLY
        );
      // getAddOnSubscriptionType only works with the main add-on identifier, which is the monthly
      const addOnSubscriptionType =
        useOnceStore().getAddOnSubscriptionType(monthlyAddOnId);
      this.status.isMoveFromAnnualToMonthly =
        addOnSubscriptionType === ADD_ON_SUBSCRIPTION_TYPE.ANNUAL_ACTIVE;
      this.status.isMoveFromMonthlyToAnnual =
        addOnSubscriptionType === ADD_ON_SUBSCRIPTION_TYPE.ANNUAL_INACTIVE;
    },
    openAddOnCheckoutSuccessModal() {
      this.status.isAddOnCheckoutSuccessModalVisible = true;
      this.status.action = ADD_ON_STATUS_ACTION.SUBSCRIBE;
    },
    openAnnualPlanRequiredModal(addOnId) {
      this.addOnId = addOnId;
      this.status.isAnnualPlanRequired = true;
    },
    openCancelAnnualModal(addOnId) {
      this.addOnId = addOnId;
      const onceStore = useOnceStore();
      const monthlyAddOnId =
        this.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
          addOnId,
          PAYMENT_FREQUENCY.MONTHLY
        );
      this.status.isTrialToCancel = onceStore.isAddOnTrialActive(
        monthlyAddOnId || addOnId
      );
      this.status.action = ADD_ON_STATUS_ACTION.CANCEL;
      if (onceStore.getFeatureFlag('wo-2614-add-on-downgrade')) {
        this.status.isAddOnDowngradeModalVisible = true;
      } else {
        if (this.isAnnualPaidMonthlyAddOn(addOnId)) {
          this.status.isCancelAnnualPaidMonthlyModalVisible = true;
        } else {
          this.status.isCancelAnnualModalVisible = true;
        }
      }
    },
    openSubscriptionEndingRestrictionModal(addOnId) {
      this.addOnId = addOnId;
      this.status.action = ADD_ON_STATUS_ACTION.CANCEL;
      this.status.isSubscriptionEndingRestrictionModalVisible = true;
    },
    openAnnualAddOnBillingScheduleChangeModal() {
      this.status.isAnnualAddOnBillingScheduleChangeModalVisible = true;
    },
    openContractAmendmentModal(addOnId) {
      this.addOnId = addOnId;
      this.status.isContractAmendmentModalVisible = true;
    },
    getAddOnName(addOnId, { short = false } = {}) {
      return short
        ? allAddOnDetails[addOnId]?.nameShort || ''
        : allAddOnDetails[addOnId]?.name || '';
    },
    getAddOnId() {
      return this.addOnId || '';
    },
    getParentAddOnName(addOnId, { short = false } = {}) {
      return short
        ? allAddOnDetails[addOnId]?.parentNameShort || ''
        : allAddOnDetails[addOnId]?.parentName || '';
    },

    /**
     * Retrieves all add-ons that share the same parent add-on.
     *
     * @param {string} parentAddOn - The parent add-on ID.
     * @return {Object} An object with add-on IDs as keys and add-on details as values.
     * @example
     * // When parent add-on has related add-ons:
     * {
     *   'hr-suite-standard': { id: 'hr-suite-standard', name: 'HR Suite Standard', ... },
     *   'hr-suite-annual': { id: 'hr-suite-annual', name: 'HR Suite Annual', ... }
     * }
     *
     * // When no add-ons share the parent:
     * {}
     */
    getAllRelatedAddOnsFromParent(parentAddOn) {
      return Object.entries(allAddOnDetails)
        .filter(([, addOn]) => addOn?.parentAddOn === parentAddOn)
        .reduce((acc, [addOnId, addOn]) => {
          acc[addOnId] = addOn;
          return acc;
        }, {});
    },

    /**
     * Retrieves related add-ons or just the add-on itself if no related add-ons are found.
     *
     * @param {string} addOnId - The ID of the add-on to retrieve related add-ons for.
     * @return {Object} An object with add-on IDs as keys and add-on details as values.
     * @example
     * // When add-on has related add-ons:
     * {
     *   'hr-suite-standard': { id: 'hr-suite-standard', name: 'HR Suite Standard', ... },
     *   'hr-suite-annual': { id: 'hr-suite-annual', name: 'HR Suite Annual', ... }
     * }
     *
     * // When add-on has no related add-ons:
     * {
     *   'standalone-addon': { id: 'standalone-addon', name: 'Standalone Add-on', ... }
     * }
     *
     * // When add-on doesn't exist:
     * {}
     */
    getRelatedAddOnsOrJustTheAddOn(addOnId) {
      const addOn = allAddOnDetails[addOnId];
      if (!addOn) {
        return {};
      }

      const parentAddOn = addOn?.parentAddOn;
      if (!parentAddOn) {
        return { [addOnId]: addOn };
      }

      const relatedAddOns = this.getAllRelatedAddOnsFromParent(parentAddOn);

      return Object.keys(relatedAddOns).length
        ? relatedAddOns
        : { [addOnId]: addOn };
    },

    /**
     * Retrieves just the list of add-on IDs from related add-ons or the add-on itself.
     *
     * @param {string} addOnId - The ID of the add-on to retrieve related add-on IDs for.
     * @return {string[]} An array of add-on IDs.
     * @example
     * // When add-on has related add-ons:
     * ['hr-suite-standard', 'hr-suite-annual']
     *
     * // When add-on has no related add-ons:
     * ['standalone-addon']
     *
     * // When add-on doesn't exist:
     * []
     */
    getRelatedAddOnIdsOrJustTheAddOnId(addOnId) {
      const relatedAddOns = this.getRelatedAddOnsOrJustTheAddOn(addOnId);
      return Object.keys(relatedAddOns);
    },

    // Check if the given add-on is the HR add-on or belongs to the same parent group.
    isHRAddOn(addOnId) {
      const hrAddOnId = addOns.HR_ADD_ON_ID;
      if (addOnId === hrAddOnId) {
        return true;
      }

      // Check if the add-on shares the same parent as the HR add-on
      const hrParent = allAddOnDetails[hrAddOnId]?.parentAddOn;
      const currentParent = allAddOnDetails[addOnId]?.parentAddOn;

      // Return false if either parent is invalid, empty, undefined, or null
      if (!hrParent || !currentParent) {
        return false;
      }

      return currentParent === hrParent;
    },

    getAddOnEquivalentPaymentFrequencyPlanIdentifier(
      addOnId,
      paymentFrequency = PAYMENT_FREQUENCY.ANNUAL_UPFRONT
    ) {
      const details = allAddOnDetails[addOnId];

      if (details?.paymentFrequency === paymentFrequency) {
        return addOnId;
      }

      // Return early if non-monthly plan has feature flag defined but disabled
      if (
        paymentFrequency !== PAYMENT_FREQUENCY.MONTHLY &&
        details?.featureFlagAnnualAddOn &&
        !useOnceStore().getFeatureFlag(details.featureFlagAnnualAddOn)
      ) {
        return '';
      }

      return details?.equivalentPaymentFrequencyPlans?.[paymentFrequency] || '';
    },
    isAvailableGlobally(addOnId) {
      // First check if the add-on itself is in addOnDataMap
      if (addOnDataMap[addOnId]?.availableGlobally) {
        return true;
      }

      // For annual add-ons, check the monthly equivalent's availability
      const monthlyEquivalentId =
        this.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
          addOnId,
          PAYMENT_FREQUENCY.MONTHLY
        );

      if (monthlyEquivalentId && monthlyEquivalentId !== addOnId) {
        return !!addOnDataMap[monthlyEquivalentId]?.availableGlobally;
      }

      return false;
    },

    /**
     * Simple method to build fromAddOns and toAddOns for tracking events
     * @param {string} fromAddOnId - The add-on being transitioned from
     * @param {string} toAddOnId - The add-on being transitioned to
     * @return {Object} Object with fromAddOns and toAddOns arrays
     */
    getFromToAddOnsForTracking(fromAddOnId, toAddOnId) {
      return {
        fromAddOns: [fromAddOnId],
        toAddOns: [toAddOnId],
      };
    },

    /**
     * Gets the add-on payment frequency before transition occurs
     * @return {string|null} The payment frequency (MONTHLY, ANNUAL_UPFRONT, or ANNUAL_PAID_MONTHLY)
     * Returns null when the customer is going from nothing to subscribing a new add-on
     * This indicates a brand-new add-on subscription, not a frequency change from an existing one.
     */
    getAddOnFrequencyBeforeTransition() {
      const isMonthlyToAnnual = this.status.isMoveFromMonthlyToAnnual;
      const isAnnualToMonthly = this.status.isMoveFromAnnualToMonthly;

      if (isMonthlyToAnnual && isAnnualToMonthly) {
        logInfo(
          'Broken state: both MonthlyToAnnual and AnnualToMonthly flags are set',
          {
            isMoveFromMonthlyToAnnual: isMonthlyToAnnual,
            isMoveFromAnnualToMonthly: isAnnualToMonthly,
          }
        );
      }

      if (isMonthlyToAnnual) {
        return PAYMENT_FREQUENCY.MONTHLY;
      }

      if (isAnnualToMonthly) {
        const onceStore = useOnceStore();
        const plansStore = usePlansStore();
        return plansStore.isAnnualUpfront(onceStore.plan)
          ? PAYMENT_FREQUENCY.ANNUAL_UPFRONT
          : PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY;
      }

      return null;
    },

    /**
     * Gets the fromAddOns and toAddOns for billing frequency change analytics
     * @param {string} targetAddOnId - The add-on being transitioned to
     * @return {Object} Object with fromAddOns and toAddOns arrays
     * If neither transition flag (isMoveFromMonthlyToAnnual nor isMoveFromAnnualToMonthly) is set,
     * returns an empty object.
     */
    getFromToAddOns(targetAddOnId) {
      const frequencyBeforeTransition =
        this.getAddOnFrequencyBeforeTransition();

      if (!frequencyBeforeTransition) {
        return {};
      }

      const onceStore = useOnceStore();
      const currentActiveAddOns = onceStore.getCurrentActiveAddOns();

      const fromAddOnId = this.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
        targetAddOnId,
        frequencyBeforeTransition
      );

      // This should never happen unless the tenant is in a broken state
      if (!currentActiveAddOns.includes(fromAddOnId)) {
        logInfo('From add-on does not exist in current active add-ons', {
          fromAddOnId,
          currentActiveAddOns,
          targetAddOnId,
        });
      }

      return this.getFromToAddOnsForTracking(fromAddOnId, targetAddOnId);
    },
    getFeatures(addOnId) {
      const onceStore = useOnceStore();
      return (
        addOnDataMap[addOnId]?.features[onceStore.country]?.[onceStore.plan] ||
        addOnDataMap[addOnId]?.features[countryCodes.WW]?.[onceStore.plan] ||
        []
      );
    },
    isFeatureAvailable(addOnId, featureKey) {
      const features = this.getFeatures(addOnId);
      return features.includes(featureKey);
    },
    getDisplayFeatures(addOnId) {
      const features = this.getFeatures(addOnId);

      const translationMap =
        addOnDataMap[addOnId]?.translations?.features || {};
      return features
        .map(featureKey => translationMap[featureKey])
        .filter(Boolean);
    },
    isAnnualAddOn(addOnId) {
      const addOn = allAddOnDetails[addOnId];
      return (
        addOn?.paymentFrequency === PAYMENT_FREQUENCY.ANNUAL_UPFRONT ||
        addOn?.paymentFrequency === PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY
      );
    },
    /**
     * Check if an add-on is free for a given plan based on pricing data.
     *
     * @param {string} addOnId - The add-on ID to check
     * @param {number} plan - The plan to check against
     * @return {boolean} True if the add-on is free for the given plan
     */
    isFreeWithPlan(addOnId, plan) {
      const onceStore = useOnceStore();

      // Check if the add-on has pricing information and the price is explicitly 0
      const pricingInfo = onceStore.getAddOnPricing(addOnId, plan);
      return pricingInfo?.pricing?.price === '0';
    },
    clearAddOn() {
      this.addOnId = null;
    },
    /**
     * Checks if the given add-on is an annual paid monthly add-on.
     *
     * @param {string} addOnId - The add-on ID to check
     * @return {boolean} True if the add-on is an annual paid monthly add-on
     */
    isAnnualPaidMonthlyAddOn(addOnId) {
      const addOn = allAddOnDetails[addOnId];
      return addOn?.paymentFrequency === PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY;
    },

    /**
     * Gets the available annual add-on IDs as an array.
     * Enforces one annual type per plan: monthly plans default to annualPaidMonthly,
     * annual plans use annual, and annualPaidMonthly plans use annualPaidMonthly.
     * Returns an array with one element for backward compatibility.
     *
     * @param {string} monthlyAddOnId - The monthly add-on ID to get the annual equivalent for.
     * @param {string} planId - Optional plan ID to check pricing against.
     * @return {string[]} Array with one available annual add-on ID, or empty array if none are available.
     * @example
     * // Possible returned values
     * ['hr-suite-annual-paid-monthly']
     * ['hr-suite-annual']
     * []
     */
    getAvailableAnnualAddOnIds(monthlyAddOnId, planId = null) {
      const onceStore = useOnceStore();
      const plansStore = usePlansStore();
      const targetPlan = planId || onceStore.plan;

      if (!this.isAnnualAddOnEnabled(monthlyAddOnId)) {
        return [];
      }

      // Get the add-on details
      const addOn = allAddOnDetails[monthlyAddOnId];
      if (!addOn?.equivalentPaymentFrequencyPlans) {
        return [];
      }

      const { annual, annualPaidMonthly } =
        addOn.equivalentPaymentFrequencyPlans;

      let targetAnnualAddOnId = '';

      // Determine which annual type to use based on plan type
      if (plansStore.isMonthly(targetPlan)) {
        targetAnnualAddOnId = annual;
      } else if (plansStore.isAnnualUpfront(targetPlan)) {
        targetAnnualAddOnId = annual;
      } else if (plansStore.isAnnualPaidMonthly(targetPlan)) {
        targetAnnualAddOnId = annualPaidMonthly;
      }

      if (!targetAnnualAddOnId) {
        return [];
      }

      return onceStore.getAddOnPricing(targetAnnualAddOnId, targetPlan)
        ? [targetAnnualAddOnId]
        : [];
    },

    /**
     * Checks if an annual add-on is enabled based on feature flags
     * @param {string} addOnId - The add-on ID to check. This add-on can be the monthly add-on or the annual add-on.
     * @return {boolean} Whether the annual add-on is enabled
     */
    isAnnualAddOnEnabled(addOnId) {
      const onceStore = useOnceStore();

      // WO-2614: AU Payroll offers annual, but not US Payroll
      if (onceStore.isUsPayroll(addOnId)) {
        return false;
      }

      const featureFlagAnnualAddOn =
        allAddOnDetails[addOnId]?.featureFlagAnnualAddOn;
      const currentAnnualAddOnEnabled =
        !featureFlagAnnualAddOn ||
        onceStore.getFeatureFlag(featureFlagAnnualAddOn);

      return currentAnnualAddOnEnabled;
    },

    /**
     * Sorts add-ons to match the order defined in ADD_ON_ORDERING
     * @param {Array<string>} addOns - Array of add-on IDs to sort
     * @return {Array<string>} New sorted array of add-on IDs
     */
    sortAddOnsByOrdering(addOns) {
      // An add-on missing from ADD_ON_ORDERING sorts last. indexOf returns -1 for it, which
      // would otherwise rank it ahead of every listed add-on.
      const rank = addOnId => {
        const index = ADD_ON_ORDERING.indexOf(addOnId);
        return index === -1 ? ADD_ON_ORDERING.length : index;
      };

      // Return a new sorted array based on the ordering in ADD_ON_ORDERING
      return [...addOns].sort((a, b) => rank(a) - rank(b));
    },

    /**
     * Sorts add-ons by billing frequency first, then by the defined ordering within each group
     * @param {Array<string>} addOns - Array of add-on IDs to sort
     * @return {Array<string>} New sorted array with annual add-ons first, then monthly
     */
    sortAddOnsByFrequencyAndOrdering(addOns) {
      // Separate add-ons by billing frequency
      const annualAddOns = addOns.filter(addOnId =>
        this.isAnnualAddOn(addOnId)
      );

      const monthlyAddOns = addOns.filter(
        addOnId => !this.isAnnualAddOn(addOnId)
      );

      // Sort each group by the defined ordering
      const sortedAnnualAddOns = this.sortAddOnsByOrdering(annualAddOns);
      const sortedMonthlyAddOns = this.sortAddOnsByOrdering(monthlyAddOns);

      // Return annual first, then monthly
      return [...sortedAnnualAddOns, ...sortedMonthlyAddOns];
    },
    resetCancellationModalState() {
      const onceStore = useOnceStore();
      this.status.isTrialToCancel = null;
      this.status.isAddOnDowngradeImplementationStateModalVisible = false;
      if (onceStore.getFeatureFlag('wo-2614-add-on-downgrade')) {
        this.status.isAddOnDowngradeModalVisible = false;
      } else {
        this.status.isCancellationModalVisible = false;
        this.status.isCancelAnnualModalVisible = false;
        this.status.isCancelAnnualPaidMonthlyModalVisible = false;
      }
    },
  },
});

export { useAddOnStore };
